import Papa from 'papaparse'
import * as XLSX from 'xlsx'
import { Workout, WorkoutExercise, WorkoutSet, WorkoutsResponse } from '../types'
import { log } from './logger'

interface HevyRow {
  title: string
  start_time: string
  end_time: string
  description: string
  exercise_title: string
  superset_id: string
  exercise_notes: string
  set_index: string
  set_type: string
  weight_kg: string
  reps: string
  distance_km: string
  duration_seconds: string
  rpe: string // TODO: Does hevy support RPE tracking? If yes implement this
}

export function parseCsvToWorkouts(content: string | ArrayBuffer, fileName?: string): WorkoutsResponse {
  // Detect if content is Excel based on fileName or content type
  const isExcel = fileName && /\.(xlsx?|xlsm|xlsb)$/i.test(fileName)
  
  let csvContent: string
  
  if (isExcel && content instanceof ArrayBuffer) {
    // Parse Excel file
    const workbook = XLSX.read(content, { type: 'array' })
    const firstSheetName = workbook.SheetNames[0]
    const worksheet = workbook.Sheets[firstSheetName]
    
    // Convert Excel to CSV
    csvContent = XLSX.utils.sheet_to_csv(worksheet, {
      // Ensure headers are preserved
      skipHidden: false,
      // Use comma as delimiter
      FS: ',',
      // Use double quotes for strings
      RS: '\n'
    })
    
    log.info(
      `Converted Excel file to CSV`,
      {
        fileName,
        sheetName: firstSheetName,
        totalSheets: workbook.SheetNames.length
      },
      'CSV_PARSER'
    )
  } else if (typeof content === 'string') {
    csvContent = content
  } else {
    throw new Error('Invalid content type. Expected string for CSV or ArrayBuffer for Excel.')
  }
  // Parse CSV with PapaParse
  const result = Papa.parse<HevyRow>(csvContent, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.trim().toLowerCase().replace(/\s+/g, '_')
  })

  if (result.errors.length > 0) {
    const errorDetails = result.errors
      .map((err, idx) => `${idx + 1}. ${err.message} (Row: ${err.row || 'unknown'})`)
      .join('\n')
    log.error('CSV parsing failed', undefined, { errors: result.errors }, 'CSV_PARSER')
    throw new Error(
      `CSV parsing failed with ${result.errors.length} error(s):\n${errorDetails}\n\nThis usually indicates a malformed CSV file. Please re-export from Hevy.`
    )
  }

  // Validate and filter CSV rows
  if (!result.data || result.data.length === 0) {
    throw new Error(
      `No data found in CSV file. The file appears to be empty or contains only headers.\n\nFile parsing result: ${JSON.stringify(result.meta, null, 2)}`
    )
  }

  const rows = result.data.filter(
    (row) =>
      row.exercise_title && (row.weight_kg || row.reps || row.distance_km || row.duration_seconds)
  )

  log.info(
    `Found ${rows.length} valid exercise sets from ${result.data.length} total rows`,
    {
      validCount: rows.length,
      totalCount: result.data.length
    },
    'CSV_PARSER'
  )

  if (rows.length === 0) {
    const sampleRows = result.data.slice(0, 3).map((row) =>
      Object.keys(row)
        .map((key) => `${key}: "${(row as unknown as Record<string, string>)[key]}"`)
        .join(', ')
    )
    throw new Error(
      `No valid workout data found in CSV. Filtered out ${result.data.length} rows with missing required fields.\n\n` +
        `Required fields: exercise_title AND (weight_kg OR reps OR distance_km OR duration_seconds)\n\n` +
        `Sample rows from your file:\n${sampleRows.join('\n')}\n\n` +
        `Please ensure your Hevy export includes workout data with exercises and performance metrics.`
    )
  }

  // Group by workout sessions
  const sessions = new Map<string, HevyRow[]>()

  rows.forEach((row) => {
    const sessionKey = `${row.title}|${row.start_time}|${row.end_time}`
    if (!sessions.has(sessionKey)) {
      sessions.set(sessionKey, [])
    }
    sessions.get(sessionKey)!.push(row)
  })

  log.info(`Found ${sessions.size} workout sessions`, { sessionCount: sessions.size }, 'CSV_PARSER')

  // Convert to workouts
  const workouts: Workout[] = []

  for (const [_sessionKey, sessionRows] of sessions.entries()) {
    const firstRow = sessionRows[0]

    // Create meaningful workout ID based on title and date
    const workoutDate = new Date(firstRow.start_time).toISOString().split('T')[0] // YYYY-MM-DD format
    const workoutTitle = firstRow.title
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, '_')
      .toLowerCase()
    const workoutId = `csv_${workoutDate}_${workoutTitle}`

    // Group exercises
    const exerciseGroups = new Map<string, HevyRow[]>()
    sessionRows.forEach((row) => {
      if (!exerciseGroups.has(row.exercise_title)) {
        exerciseGroups.set(row.exercise_title, [])
      }
      exerciseGroups.get(row.exercise_title)!.push(row)
    })

    // Convert exercises
    const exercises: WorkoutExercise[] = []
    let exerciseIndex = 0

    for (const [exerciseName, exerciseRows] of exerciseGroups.entries()) {
      // Sort sets by index
      exerciseRows.sort((a, b) => parseInt(a.set_index || '0') - parseInt(b.set_index || '0'))

      const sets: WorkoutSet[] = exerciseRows.map((row, index) => ({
        index: parseInt(row.set_index) || index,
        type: row.set_type || 'normal',
        weight_kg: parseFloat(row.weight_kg) || 0,
        reps: parseInt(row.reps) || 0,
        distance_meters: row.distance_km ? parseFloat(row.distance_km) * 1000 : null,
        duration_seconds: row.duration_seconds ? parseInt(row.duration_seconds) : null,
        rpe: row.rpe ? parseInt(row.rpe) : null,
        custom_metric: null
      }))

      // Generate template ID for CSV exercises
      const templateId = `csv_${exerciseName.replace(/\s+/g, '_').toLowerCase()}`

      exercises.push({
        index: exerciseIndex++,
        title: exerciseName,
        notes: exerciseRows[0].exercise_notes || '',
        exercise_template_id: templateId,
        superset_id: exerciseRows[0].superset_id || null,
        sets
        // Note: Muscle group processing will be handled by DataProcessor.ensureWorkoutsProcessed()
      })
    }

    workouts.push({
      id: workoutId,
      title: firstRow.title,
      description: firstRow.description || '',
      start_time: new Date(firstRow.start_time).toISOString(),
      end_time: new Date(firstRow.end_time).toISOString(),
      updated_at: new Date(firstRow.start_time).toISOString(),
      created_at: new Date(firstRow.start_time).toISOString(),
      exercises
    })
  }

  // Sort by date (newest first)
  workouts.sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime())

  log.info(
    `Parsed ${workouts.length} workouts`,
    {
      parsedCount: workouts.length
    },
    'CSV_PARSER'
  )

  return {
    page: 1,
    page_count: 1,
    workouts: workouts
  }
}

export function validateCsvFormat(content: string | ArrayBuffer, fileName?: string): { isValid: boolean; error?: string } {
  try {
    let csvContent: string
    
    // Handle Excel files
    const isExcel = fileName && /\.(xlsx?|xlsm|xlsb)$/i.test(fileName)
    
    if (isExcel && content instanceof ArrayBuffer) {
      // Parse Excel file
      const workbook = XLSX.read(content, { type: 'array' })
      const firstSheetName = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[firstSheetName]
      
      // Convert Excel to CSV for validation
      csvContent = XLSX.utils.sheet_to_csv(worksheet)
      
      if (!csvContent?.trim()) {
        return { isValid: false, error: 'Excel file is empty or contains only whitespace' }
      }
    } else if (typeof content === 'string') {
      csvContent = content
      if (!csvContent?.trim()) {
        return { isValid: false, error: 'CSV file is empty or contains only whitespace' }
      }
    } else {
      return { isValid: false, error: 'Invalid content type. Expected string for CSV or ArrayBuffer for Excel.' }
    }

    // Check for common CSV issues
    const lines = csvContent.split('\n')
    if (lines.length < 2) {
      return {
        isValid: false,
        error: `CSV file contains only ${lines.length} line(s). Expected at least 2 lines (header + data).`
      }
    }

    const result = Papa.parse(csvContent, {
      header: true,
      preview: 3, // Parse more rows for better validation
      transformHeader: (header) => header.trim().toLowerCase().replace(/\s+/g, '_')
    })

    if (result.errors.length > 0) {
      const errorDetails = result.errors
        .map((err) => `${err.message} (Row: ${err.row || 'unknown'})`)
        .join(', ')
      return { isValid: false, error: `CSV format errors: ${errorDetails}` }
    }

    const headers = Object.keys(result.data[0] || {})
    const requiredHeaders = ['title', 'start_time', 'exercise_title']
    const missingHeaders = []

    for (const required of requiredHeaders) {
      if (!headers.some((h) => h.includes(required))) {
        missingHeaders.push(required)
      }
    }

    if (missingHeaders.length > 0) {
      return {
        isValid: false,
        error:
          `Missing required columns: ${missingHeaders.join(', ')}\n\n` +
          `Found columns: ${headers.join(', ')}\n\n` +
          `Expected Hevy CSV format. Please ensure you're using a direct export from Hevy.`
      }
    }

    // Check if we have any actual data
    if (result.data.length === 0) {
      return { isValid: false, error: 'CSV file contains headers but no data rows' }
    }

    // Check for empty required fields in first few rows
    const emptyFieldIssues: string[] = []
    result.data.slice(0, 3).forEach((row: unknown, idx) => {
      const typedRow = row as HevyRow
      if (!typedRow.title) emptyFieldIssues.push(`Row ${idx + 1}: missing title`)
      if (!typedRow.start_time) emptyFieldIssues.push(`Row ${idx + 1}: missing start_time`)
      if (!typedRow.exercise_title) emptyFieldIssues.push(`Row ${idx + 1}: missing exercise_title`)
    })

    if (emptyFieldIssues.length > 0) {
      return {
        isValid: false,
        error: `Data validation issues:\n${emptyFieldIssues.join('\n')}\n\nPlease ensure your Hevy export is complete.`
      }
    }

    return { isValid: true }
  } catch (error) {
    return {
      isValid: false,
      error: `CSV validation error: ${error instanceof Error ? error.message : 'Unknown error'}\n\nThis may indicate a corrupted or non-standard CSV file.`
    }
  }
}
